import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";
import type { GaSalesItem, GaSalesTrend } from "@/api/dashboard";

// Katalog-Spalte „Verkauf 30 T.“: Stueckzahl der letzten 30 Tage, Tendenz gegen die 30 Tage
// davor und 12 Balken à 5 Tage. Die linke Haelfte (Vorzeitraum) bleibt grau, die rechte
// (letzte 30 Tage) traegt die Tendenzfarbe – so steht der Vergleich direkt im Bild.

const TREND_TEXT: Record<GaSalesTrend, string> = {
  up: "steigend",
  down: "fallend",
  flat: "gleichbleibend",
  none: "kein Verkauf",
};

const TREND_CLASS: Record<GaSalesTrend, string> = {
  up: "text-success",
  down: "text-coral",
  flat: "text-ink-2",
  none: "text-muted-2",
};

function fmtStueck(n: number): string {
  return n.toLocaleString("de-DE", { maximumFractionDigits: 3 });
}

export function verkaufTitel(s: GaSalesItem): string {
  const pct =
    s.change_pct === null ? "" : ` (${s.change_pct > 0 ? "+" : ""}${s.change_pct.toLocaleString("de-DE")} %)`;
  return `Letzte 30 Tage: ${fmtStueck(s.sold_30d)} Stk. · 30 Tage davor: ${fmtStueck(s.sold_prev_30d)} Stk. · ${TREND_TEXT[s.trend]}${pct}`;
}

const BAR_W = 3;
const GAP = 1;
const MID_GAP = 3;
const H = 16;

function Balken({ buckets, trend }: { buckets: number[]; trend: GaSalesTrend }) {
  const half = buckets.length / 2;
  // Gutschrift-Ueberhang (negativ) zeichnet als leerer Abschnitt.
  const values = buckets.map((v) => Math.max(0, v));
  const max = Math.max(...values, 0);
  const width = buckets.length * (BAR_W + GAP) - GAP + MID_GAP;
  return (
    <svg
      aria-hidden
      width={width}
      height={H}
      viewBox={`0 0 ${width} ${H}`}
      className={`shrink-0 ${TREND_CLASS[trend]}`}
    >
      {values.map((v, i) => {
        const x = i * (BAR_W + GAP) + (i >= half ? MID_GAP : 0);
        // Leere Abschnitte als 1-px-Grundlinie, damit die Zeitachse lesbar bleibt.
        const h = max > 0 && v > 0 ? Math.max(2, Math.round((v / max) * H)) : 1;
        return (
          <rect
            key={i}
            x={x}
            y={H - h}
            width={BAR_W}
            height={h}
            rx={0.5}
            fill={i < half || v <= 0 ? "var(--muted-2)" : "currentColor"}
          />
        );
      })}
    </svg>
  );
}

const PFEIL = { up: ArrowUpRight, down: ArrowDownRight, flat: ArrowRight } as const;

export function VerkaufZelle({ sales, pending }: { sales: GaSalesItem | undefined; pending: boolean }) {
  if (!sales) {
    return <span className="text-muted">{pending ? "…" : "—"}</span>;
  }
  const Pfeil = sales.trend === "none" ? null : PFEIL[sales.trend];
  return (
    <span
      className="inline-flex items-center justify-end gap-2"
      title={verkaufTitel(sales)}
      data-testid="verkauf-30t"
    >
      <Balken buckets={sales.buckets} trend={sales.trend} />
      <span className={`min-w-12 text-right font-mono ${sales.sold_30d === 0 ? "text-ink-2" : "text-ink"}`}>
        {fmtStueck(sales.sold_30d)}
      </span>
      <span className={`inline-flex size-3.5 ${TREND_CLASS[sales.trend]}`}>
        {Pfeil && <Pfeil className="size-3.5" strokeWidth={2.25} aria-hidden />}
      </span>
      <span className="sr-only">{TREND_TEXT[sales.trend]}</span>
    </span>
  );
}
